import React from 'react'
import {ReactElement, useEffect} from 'react'
import {useLocation} from 'react-router'

const maxAttempts = 3
const attemptsInterval = 200
const fbPixelId = process.env.REACT_APP_PIXEL_ID as string

export interface FacebookPixelContextProps {
  sendFbPixel: (action: string, data?: object) => void
}

export const FacebookPixelContext = React.createContext<
  FacebookPixelContextProps | undefined
>(undefined)

export function FacebookPixelProvider(props: {children: ReactElement}) {
  const location = useLocation()

  const sendFbPixel = (action: string, data?: object) => {
    fbq('track', action, data)
  }

  useEffect(() => {
    setFbCCookie()

    const script = document.createElement('script')
    script.innerHTML = injectFacebookPixelCode
    document.head.appendChild(script)
    fbq('init', fbPixelId)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  useEffect(() => {
    fbq('track', 'PageView')
  }, [location.pathname])

  return (
    <FacebookPixelContext.Provider
      value={{
        sendFbPixel,
      }}
    >
      {props.children}
    </FacebookPixelContext.Provider>
  )
}

export function useFacebookPixel() {
  const context = React.useContext(FacebookPixelContext)
  if (context === undefined) {
    throw new Error(
      'useFacebookPixel must be used within FacebookPixelProvider',
    )
  }
  return context
}

function setFbCCookie() {
  const urlParams = new URLSearchParams(window.location.search)
  const fbclid = urlParams.get('fbclid')

  if (fbclid) {
    const timestamp = Math.floor(Date.now())
    const fbc = `fb.1.${timestamp}.${fbclid}`

    document.cookie = `_fbc=${fbc}; path=/; max-age=${90 * 24 * 60 * 60}; `
  }
}

export function getFbc(): string | null {
  const cookies = document.cookie.split('; ')
  for (const cookie of cookies) {
    const [name, value] = cookie.split('=')
    if (name === '_fbc') {
      console.log('Found _fbc cookie:', value)
      return value
    }
  }

  console.log('No _fbc cookie found')
  return null
}

const injectFacebookPixelCode = `
            !(function (f, b, e, v, n, t, s) {
                if (f.fbq) return;
                n = f.fbq = function () {
                    n.callMethod
                        ? n.callMethod.apply(n, arguments)
                        : n.queue.push(arguments);
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = !0;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = !0;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t, s);
            })(
                window,
                document,
                'script',
                'https://connect.facebook.net/en_US/fbevents.js'
            );
          
        `

function fbq(event: string, action: string, data: object = {}) {
  if (sendfbq(event, action, data)) return

  let attempts = 0

  const interval = setInterval(() => {
    if (sendfbq(event, action, data)) {
      clearInterval(interval)
      return
    }

    attempts++
    if (attempts >= maxAttempts) {
      clearInterval(interval)
      console.log(
        `Facebook Pixel is not initialized after ${maxAttempts} attempts.`,
      )
    }
  }, attemptsInterval)
}

function sendfbq(event: string, action: string, data: object = {}) {
  if (typeof window !== 'undefined' && (window as any).fbq) {
    ;(window as any).fbq(event, action, data)
    return true
  }

  return false
}
